import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Button } from "@groot/ui/button";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Input } from "@groot/ui/input";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { useConfirm } from "@groot/ui/primitives";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import { type Account, AccountType } from "@/types";
import { FileText, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  [AccountType.BANK]: "Bank",
  [AccountType.CREDIT_CARD]: "Credit Card",
  [AccountType.CASH]: "Cash",
  [AccountType.DIGITAL_WALLET]: "Digital Wallet",
  [AccountType.LOAN]: "Loan",
  [AccountType.CRYPTOCURRENCY]: "Crypto",
  [AccountType.OTHER]: "Other",
};

export function Accounts() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "archived">("active");
  const [filterType, setFilterType] = useState<AccountType | "all">("all");

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getAccounts();
      setAccounts(data);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load accounts",
      });
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial load
  useEffect(() => {
    loadAccounts();
  }, []);

  const handleDelete = async (id: number, name: string) => {
    if (
      !(await confirm({
        title: "Confirm Action",
        description: `Are you sure you want to delete "${name}"?`,
      }))
    ) {
      return;
    }

    try {
      await apiClient.deleteAccount(id);
      toast({
        title: "Success",
        description: "Account deleted successfully",
      });
      loadAccounts();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete account",
      });
    }
  };

  // Filter and search accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          account.accountName.toLowerCase().includes(query) ||
          account.accountNumber?.toLowerCase().includes(query) ||
          account.lastFourAccountNumber?.includes(query);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filterStatus === "active" && account.isArchived) return false;
      if (filterStatus === "archived" && !account.isArchived) return false;

      // Type filter
      if (filterType !== "all" && account.type !== filterType) return false;

      // Hide deleted accounts
      if (account.isDeleted) return false;

      return true;
    });
  }, [accounts, searchQuery, filterStatus, filterType]);

  const getAccountIcon = (account: Account) => {
    if (account.iconUrl) {
      return (
        <img
          src={account.iconUrl}
          alt={account.accountName}
          className="size-6 rounded object-contain flex-shrink-0"
        />
      );
    }

    return (
      <div className="size-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <span className="text-muted-foreground font-medium text-xs">
          {account.accountName.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  };

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Accounts"
        description={`${filteredAccounts.length} ${filteredAccounts.length === 1 ? "account" : "accounts"}`}
        actions={
          <Link to="/accounts/new">
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              New account
            </Button>
          </Link>
        }
        className="mb-8"
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70" />
          <Input
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-input focus:border-input focus:ring-0"
          />
        </div>
        <div className="flex gap-3">
          <Select
            value={filterType}
            onValueChange={(value: AccountType | "all") => setFilterType(value)}
          >
            <SelectTrigger className="w-full sm:w-[140px] border-input">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value={AccountType.BANK}>Bank</SelectItem>
              <SelectItem value={AccountType.CREDIT_CARD}>Credit Card</SelectItem>
              <SelectItem value={AccountType.CASH}>Cash</SelectItem>
              <SelectItem value={AccountType.DIGITAL_WALLET}>Digital Wallet</SelectItem>
              <SelectItem value={AccountType.LOAN}>Loan</SelectItem>
              <SelectItem value={AccountType.CRYPTOCURRENCY}>Crypto</SelectItem>
              <SelectItem value={AccountType.OTHER}>Other</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filterStatus}
            onValueChange={(value: "all" | "active" | "archived") => setFilterStatus(value)}
          >
            <SelectTrigger className="w-full sm:w-[120px] border-input">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block">
        {loading ? (
          <LoadingSpinner size="lg" className="py-20" />
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4">
              {searchQuery || filterStatus !== "active" || filterType !== "all"
                ? "No accounts found"
                : "No accounts yet"}
            </p>
            {!searchQuery && filterStatus === "active" && filterType === "all" && (
              <Link to="/accounts/new">
                <Button variant="outline" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create your first account
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted hover:bg-muted">
                  <TableHead className="font-medium text-foreground/80">Name</TableHead>
                  <TableHead className="font-medium text-foreground/80">Type</TableHead>
                  <TableHead className="font-medium text-foreground/80 text-right">
                    Balance
                  </TableHead>
                  <TableHead className="font-medium text-foreground/80">Last 4 digits</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow
                    key={account.id}
                    className="cursor-pointer hover:bg-muted border-border"
                    onClick={() => navigate(`/accounts/${account.id}`)}
                  >
                    <TableCell className="font-medium text-foreground">
                      <div className="flex items-center gap-3">
                        {getAccountIcon(account)}
                        <div className="flex items-center gap-2">
                          <span>{account.accountName}</span>
                          {account.isArchived && (
                            <span className="text-xs text-muted-foreground/70">(Archived)</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {ACCOUNT_TYPE_LABELS[account.type]}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-medium text-foreground">
                        <AmountDisplay
                          amount={account.currentBalance || 0}
                          currencyCode={account.currencyCode}
                        />
                      </div>
                      {account.type === AccountType.CREDIT_CARD && !!account.creditLimit && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          of{" "}
                          <AmountDisplay
                            amount={account.creditLimit}
                            currencyCode={account.currencyCode}
                          />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-[0.9em] text-muted-foreground">
                      {account.lastFourAccountNumber || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/accounts/${account.id}/statements`);
                          }}
                          title="View Statements"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(account.id, account.accountName);
                          }}
                          title="Delete Account"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden">
        {loading ? (
          <LoadingSpinner size="lg" className="py-20" />
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4">
              {searchQuery || filterStatus !== "active" || filterType !== "all"
                ? "No accounts found"
                : "No accounts yet"}
            </p>
            {!searchQuery && filterStatus === "active" && filterType === "all" && (
              <Link to="/accounts/new">
                <Button variant="outline" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create your first account
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAccounts.map((account) => (
              <div
                key={account.id}
                className="w-full border border-border rounded-lg hover:bg-muted"
              >
                <div className="flex items-start justify-between gap-3 p-4">
                  <button
                    type="button"
                    className="flex-1 text-left"
                    onClick={() => navigate(`/accounts/${account.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/accounts/${account.id}`);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {getAccountIcon(account)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground truncate">
                            {account.accountName}
                          </span>
                          {account.isArchived && (
                            <span className="text-xs text-muted-foreground/70 whitespace-nowrap">
                              (Archived)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
                            {ACCOUNT_TYPE_LABELS[account.type]}
                          </span>
                          {account.lastFourAccountNumber && (
                            <span className="text-xs text-muted-foreground font-mono">
                              •••• {account.lastFourAccountNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm text-muted-foreground">Balance</span>
                        <div className="text-right">
                          <div className="font-medium text-foreground">
                            <AmountDisplay
                              amount={account.currentBalance || 0}
                              currencyCode={account.currencyCode}
                            />
                          </div>
                          {account.type === AccountType.CREDIT_CARD && !!account.creditLimit && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              of{" "}
                              <AmountDisplay
                                amount={account.creditLimit}
                                currencyCode={account.currencyCode}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                      onClick={() => navigate(`/accounts/${account.id}/statements`)}
                      title="View Statements"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                      onClick={() => handleDelete(account.id, account.accountName)}
                      title="Delete Account"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
